import React, { useState } from "react";
import { Link } from "react-router";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import { getCaseLink, getProductData } from "@/src/services/productService";
import { Helmet } from "react-helmet-async";
import { apiCall } from "@/src/services/apiClient";
import HintBox from "@/src/components/Box/HintBox/HintBox";
import { fromGender } from "@/src/services/formServiceFunctions";
import Button from "@mui/material/Button";
import { STATUS_POWER_OF_ATTORNEY_GRANTED } from "@/src/services/divorce/leadStatus";
import PowerOfAttorney from "@/src/components/Form/Divorce/PowerOfAttorney";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import Typography from "@mui/material/Typography";
import ScrollToTop from "@/src/services/ScrollToTop";

const PowerOfAttorneyPageDivorce = () => {
  const { productId } = useProductParams();
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const productApiUri = getProductData("divorce", "apiUri");
  const { data: divorce, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const { customer } = useCustomer();
  const formService = getProductData("divorce", "formService");
  const queryClient = useQueryClient();

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  if (isComplete || divorce <= STATUS_POWER_OF_ATTORNEY_GRANTED || divorce.powerOfAttorney !== null) {
    return (
      <LayoutProtectedRoutes>
        <ScrollToTop />
        <HintBox headline={"Vielen Dank!"}>
          <Typography>Wir haben Ihre Vollmacht gespeichert und bearbeiten Ihre Scheidung entsprechend weiter.</Typography>
          <Button sx={{ marginTop: "1rem" }} variant={"outlined"} color={"primary"} component={Link} to={getCaseLink(divorce)}>
            Zurück zu meinem Fall
          </Button>
        </HintBox>
      </LayoutProtectedRoutes>
    );
  }

  const handleSubmit = async () => {
    setIsLoadingInternal(true);
    await apiCall("PATCH", "/divorces/" + divorce.id + "/power_of_attorney", {
      body: JSON.stringify({
        powerOfAttorney: null,
      }),
    });
    await queryClient.invalidateQueries({ queryKey: queryKeys.collections("/inquiry_to_customers") });
    setIsLoadingInternal(false);
    setIsComplete(true);
    queryClient.invalidateQueries({
      queryKey: ["/divorces", productId],
    });
  };

  return (
    <FormProvider initialValues={formService.prepareFormData(divorce, customer)} submitAction={handleSubmit}>
      <LayoutProtectedRoutes>
        <Helmet>
          <title>Vollmacht | Legalbird</title>
        </Helmet>
        <HintBox headline={"Bitte erteilen Sie Ihre Vollmacht"}>
          <Typography>
            Achtung: Auf dieser Vollmacht muss{" "}
            <strong>
              {fromGender(divorce.client.gender)} {divorce.client.givenName} {divorce.client.familyName}
            </strong>{" "}
            unterzeichnen, damit wir weiter tätig werden können.
          </Typography>
        </HintBox>
        <PowerOfAttorney product={divorce} submitProps={{ isLoading: isLoadingInternal, isMinimal: true }} />
      </LayoutProtectedRoutes>
    </FormProvider>
  );
};
export default PowerOfAttorneyPageDivorce;
